import { RoutePaths } from "../RoutePaths";

function isOnForceLoginUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  return (
    params.has(RoutePaths.FORCE_LOGIN_QUERY_PARAM) ||
    params.has(RoutePaths.FORCE_SIGNUP_QUERY_PARAM)
  );
}

/**
 * True if the current document was reached via a back/forward navigation (browser "Back"/"Forward"
 * or a bfcache restore) rather than a fresh link click or reload. Uses the Navigation Timing API
 * and defaults to false when it is unavailable.
 */
function isBackForwardNavigation(): boolean {
  const [navigation] = performance.getEntriesByType("navigation");
  return !!(
    navigation &&
    "type" in navigation &&
    navigation.type === "back_forward"
  );
}

function goToLandingPage(): void {
  // replace() so the dead login-trigger entry (e.g. "/?login=true") does not linger in history.
  window.location.replace(RoutePaths.LANDING_PAGE);
}

/**
 * Firefox/Vivaldi: pressing "Back" on the Auth0 login page does a fresh reload of the force-login
 * URL ("/?login=true"). Left alone, the app would immediately redirect to Auth0 again (login loop).
 * If we are on the force-login URL AND arrived here via a back/forward navigation, the user is
 * backing out of login — send them to the landing page and return true so the caller aborts the
 * redirect to Auth0.
 *
 * Stateless on purpose: an earlier flag-based version went stale when the user reached the static
 * landing page without re-running app code, which then mis-classified the NEXT login click as a
 * back-out.
 */
export function redirectToLandingIfBackingOutOfLogin(): boolean {
  if (isOnForceLoginUrl() && isBackForwardNavigation()) {
    goToLandingPage();
    return true;
  }
  return false;
}

/**
 * Chrome: pressing "Back" on the Auth0 login page restores the force-login page from the
 * back/forward cache (bfcache) WITHOUT re-running any JavaScript, so redirectToLandingIfBackingOutOf
 * Login() never runs and the user is stuck on a blank page. Detect that exact restore
 * (event.persisted while on the force-login URL) and send them to the landing page instead.
 *
 * Must be called once at initial document load so the listener is retained in the frozen bfcache
 * snapshot and fires on restore.
 */
export function installAuth0LoginBackoutGuard(): void {
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && isOnForceLoginUrl()) {
      goToLandingPage();
    }
  });
}
